import React, { useState, useEffect } from 'react';
import './App.css';
import JsonEditor from './JsonEditor';
import DiffChecker from './DiffChecker';
import SaveModal from './SaveModal';
import LoadModal from './LoadModal';
import { FaSave, FaFolderOpen, FaMoon, FaSun } from 'react-icons/fa';
import { Analytics } from "@vercel/analytics/react";
import { HelmetProvider, Helmet } from 'react-helmet-async';

function App() {
  const [activeTab, setActiveTab] = useState('diff');
  const [inputJson, setInputJson] = useState('');
  const [error, setError] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true); // Default to dark mode

  // Diff checker state
  const [json1, setJson1] = useState('');
  const [json2, setJson2] = useState('');
  const [keysToDelete, setKeysToDelete] = useState('');
  const [canonicalJson1, setCanonicalJson1] = useState('');
  const [canonicalJson2, setCanonicalJson2] = useState('');

  const handleSaveSuccess = (savedFile) => {
    console.log('File saved successfully:', savedFile);
    // You could add a toast notification here
  };

  const handleLoadFile = (jsonContent) => {
    setInputJson(jsonContent);
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Apply dark mode class to document body
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  return (
    <HelmetProvider>
      <div className="App">
        <Helmet>
          <title>{activeTab === 'formatter' ? 'JSON Formatter Online - Format JSON Free | JSON Diff Pro Max' : 'JSON Compare Online Free - Compare 2 JSON Files | JSON Diff Tool'}</title>
          <meta name="description" content={activeTab === 'formatter'
            ? 'Format JSON online free with JSON Diff Pro Max. Pretty print, validate, and format JSON objects instantly with our free JSON formatter tool.'
            : 'Compare JSON objects online free with JSON Diff Pro Max. Fast, accurate JSON compare tool for Windows. Compare 2 JSON files, objects, and schemas instantly with deep nested comparison.'
          } />
          <meta name="keywords" content={activeTab === 'formatter'
            ? 'json formatter, format json online, json pretty print, json validator, json beautifier, online json formatter, json format tool'
            : 'json compare online, json compare tool windows, compare json objects online, compare json online free, compare 2 json online, json compare tool online, compare 2 json files online, jsondiff.com, json comparator online, compare json files online, json compare tool download, compare two json objects online, jsondiff, online json compare tool, compare two json online, json editor compare, json difference online, online compare json, json comparison tool, json diff, json diff tool, json compare tool, jsondiff, compare json schema, json online compare, json diff javascript, json diff online, online json diff, diff json online, diff online json, json comparison online, compare two json files online, compare two jsons online, json compare online tool, json compare tools, json diff viewer, json file compare online, json difference, tool to compare two json files, api response comparison tool, compare two json file online'
          } />
          <meta property="og:title" content={activeTab === 'formatter'
            ? 'JSON Formatter Online - Format JSON Free | JSON Diff Pro Max'
            : 'JSON Compare Online Free - Compare 2 JSON Files | JSON Diff Tool'
          } />
          <meta property="og:description" content={activeTab === 'formatter'
            ? 'Format JSON online free with JSON Diff Pro Max. Pretty print, validate, and format JSON objects instantly.'
            : 'Compare JSON objects online free with JSON Diff Pro Max. Fast, accurate JSON compare tool for Windows. Compare 2 JSON files, objects, and schemas instantly with deep nested comparison.'
          } />
          <meta name="twitter:title" content={activeTab === 'formatter'
            ? 'JSON Formatter Online - Format JSON Free'
            : 'JSON Compare Online Free - JSON Diff Tool'
          } />
          <meta name="twitter:description" content={activeTab === 'formatter'
            ? 'Format JSON online free with JSON Diff Pro Max. Pretty print, validate, and format JSON objects instantly.'
            : 'Compare 2 JSON files online free. Fast, accurate JSON diff tool with deep nested comparison.'
          } />
        </Helmet>
        <header className="App-header">
          <h1 style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
            {activeTab === 'formatter' ? 'JSON Formatter Online - Format JSON Free' : 'JSON Compare Online Free - Compare 2 JSON Files'}
          </h1>
          <h2 style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
            {activeTab === 'formatter' ? 'Free JSON Formatter and Beautifier Tool' : 'Best JSON Diff Tool for Windows and Online'}
          </h2>
          <div className="tab-navigation">
            <button
              className={`tab-btn ${activeTab === 'formatter' ? 'active' : ''}`}
              onClick={() => setActiveTab('formatter')}
            >
              JSON Formatter Online
            </button>
            <button
              className={`tab-btn ${activeTab === 'diff' ? 'active' : ''}`}
              onClick={() => setActiveTab('diff')}
            >
              JSON Compare Tool Online
            </button>
            <button
              className="dark-mode-toggle"
              onClick={toggleDarkMode}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDarkMode ? <FaSun /> : <FaMoon />}
            </button>
          </div>
          <div style={{ padding: '5px' }}>
            <p>
              {activeTab === 'formatter'
                ? 'Format JSON online free with our JSON formatter tool. Edit your JSON in the editor below and watch it format automatically as you type'
                : 'Compare JSON objects online free! Paste two JSON objects and watch them get canonicalized and compared automatically in real-time. Our JSON compare tool provides deep nested accurate comparison for Windows and all platforms.'
              }
            </p>
            {/* SEO Content for Crawlers */}
            <div style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
              <h3>JSON Compare Online Free - Best JSON Diff Tool</h3>
              <p>Compare JSON objects online free with our advanced JSON diff tool. Perfect for developers who need to compare 2 JSON files online, compare JSON objects online, or use a JSON compare tool for Windows. Our JSON comparator online provides deep nested comparison with canonicalization.</p>
              <h4>Features of our JSON Compare Tool Online:</h4>
              <ul>
                <li>Compare 2 JSON files online free</li>
                <li>JSON compare tool Windows compatible</li>
                <li>Compare JSON objects online with deep nested analysis</li>
                <li>JSON diff tool with real-time highlighting</li>
                <li>JSON editor compare functionality</li>
                <li>API response comparison tool</li>
                <li>JSON schema comparison</li>
              </ul>
              <h4>How to Compare Two JSON Files Online:</h4>
              <p>Simply paste your JSON objects into our online JSON diff tool and get instant, accurate comparisons. Our JSON compare online free tool works on all platforms including Windows, macOS, and Linux.</p>
            </div>
          </div>
        </header>


        {activeTab === 'formatter' && (
          <div className="formatter-container">
            <div className="json-editor-section">
              <div className="editor-header">
                <h3>JSON Editor Online - Format JSON Free</h3>
                <div className="editor-actions">
                  <button
                    className="action-btn load-btn"
                    onClick={() => setShowLoadModal(true)}
                    title="Load saved JSON file"
                  >
                    <FaFolderOpen />
                    Load
                  </button>
                  <button
                    className="action-btn save-btn"
                    onClick={() => setShowSaveModal(true)}
                    disabled={!inputJson.trim()}
                    title="Save current JSON to local storage"
                  >
                    <FaSave />
                    Save
                  </button>
                </div>
              </div>
              <div className="json-editor-container">
                <JsonEditor
                  jsonValue={inputJson}
                  onJsonChange={setInputJson}
                  onError={setError}
                  isDarkMode={isDarkMode}
                />
              </div>
              {error && <div className="error-message">{error}</div>}
            </div>
          </div>
        )}

        {activeTab === 'diff' && (
          <DiffChecker
            json1={json1}
            json2={json2}
            keysToDelete={keysToDelete}
            canonicalJson1={canonicalJson1}
            canonicalJson2={canonicalJson2}
            onJson1Change={setJson1}
            onJson2Change={setJson2}
            onKeysToDeleteChange={setKeysToDelete}
            onCanonicalJson1Change={setCanonicalJson1}
            onCanonicalJson2Change={setCanonicalJson2}
            isDarkMode={isDarkMode}
          />
        )}

        {/* Modals */}
        <SaveModal
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          jsonContent={inputJson}
          onSaveSuccess={handleSaveSuccess}
          isDarkMode={isDarkMode}
        />

        <LoadModal
          isOpen={showLoadModal}
          onClose={() => setShowLoadModal(false)}
          onLoadFile={handleLoadFile}
          isDarkMode={isDarkMode}
        />
        <Analytics />
      </div>
    </HelmetProvider>
  );
}

export default App;
